import { Card, DataTable, InlineStack, Link, SkeletonBodyText, Text } from '@shopify/polaris';
import { CardHeading } from '../../../../shared/components/cards/CardHeading';
import { formatDays, formatInteger, formatPercent, toNumber } from '../deep/analyticsHelpers';
import { formatEur } from './chartTheme';

/*
  The cash position from open documents, in the layout the CFO asked for:
  money that still has to leave (supplier invoices, credit notes we issued) and money that still has
  to come in (customer invoices, credit notes we received), each split in "overdue" and "total open".
  Only the part that is still open counts, never the full document amounts.
*/

const ROWS = [
  { key: 'invoices_in', label: 'Invoices IN', hint: 'we pay suppliers', sign: -1 },
  { key: 'creditnotes_out', label: 'Credit notes OUT', hint: 'we refund customers', sign: -1 },
  { key: 'invoices_out', label: 'Invoices OUT', hint: 'customers pay us', sign: 1 },
  { key: 'creditnotes_in', label: 'Credit notes IN', hint: 'suppliers refund us', sign: 1 }
];

function Signed({ value, strong = false }) {
  const number = toNumber(value);
  const tone = number < 0 ? 'critical' : number > 0 ? 'success' : 'subdued';
  return (
    <Text as="span" tone={tone} fontWeight={strong ? 'semibold' : 'regular'}>
      {number === 0 ? '—' : formatEur(number)}
    </Text>
  );
}

export default function OpenPositionsCard({ summary, cashflowUrl }) {
  const positions = summary?.openPositions || [];
  const byKey = Object.fromEntries(positions.map((row) => [row.key, row]));
  const value = (key, field) => toNumber(byKey[key]?.[field]);

  const rows = ROWS.map((row) => [
    <span>
      <Text as="span" fontWeight="semibold">
        {row.label}
      </Text>
      <br />
      <Text as="span" variant="bodySm" tone="subdued">
        {`${row.hint} · ${formatInteger(value(row.key, 'count'))} open`}
      </Text>
    </span>,
    <Signed value={row.sign * value(row.key, 'overdue')} />,
    <Signed value={row.sign * value(row.key, 'open')} />
  ]);

  const netOverdue = ROWS.reduce((sum, row) => sum + row.sign * value(row.key, 'overdue'), 0);
  const netOpen = ROWS.reduce((sum, row) => sum + row.sign * value(row.key, 'open'), 0);

  const receivablesOpen = value('invoices_out', 'open');
  const receivablesOverdue = value('invoices_out', 'overdue');
  const payablesOpen = value('invoices_in', 'open');
  const payablesOverdue = value('invoices_in', 'overdue');
  const dso = summary?.trailing365?.revenueIncl ? (receivablesOpen / toNumber(summary.trailing365.revenueIncl)) * 365 : null;
  const dpo = summary?.trailing365?.costsIncl ? (payablesOpen / toNumber(summary.trailing365.costsIncl)) * 365 : null;

  return (
    <Card>
      <InlineStack align="space-between" blockAlign="start" gap="400">
        <div>
          <CardHeading title="Open positions" />
          <Text as="p" variant="bodySm" tone="subdued">
            What still has to be paid and collected, open part only. Negative leaves the account, positive comes in.
          </Text>
        </div>
        {cashflowUrl ? (
          <Link removeUnderline url={cashflowUrl}>
            Aging & largest items
          </Link>
        ) : null}
      </InlineStack>
      {!summary ? (
        <div style={{ marginTop: 16 }}>
          <SkeletonBodyText lines={6} />
        </div>
      ) : (
        <div style={{ marginTop: 8 }}>
          <DataTable
            verticalAlign="middle"
            increasedTableDensity
            columnContentTypes={['text', 'numeric', 'numeric']}
            headings={[<b>Document</b>, <b>Overdue (open part)</b>, <b>Total open</b>]}
            rows={rows}
            totals={['', <Signed value={netOverdue} strong />, <Signed value={netOpen} strong />]}
            totalsName={{ singular: 'Net', plural: 'Net' }}
            showTotalsInFooter
          />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginTop: 16 }}>
            <div>
              <Text as="p" variant="bodySm" tone="subdued">
                Overdue receivables
              </Text>
              <Text as="p" fontWeight="semibold">
                {receivablesOpen ? formatPercent(receivablesOverdue / receivablesOpen) : '—'}
              </Text>
              <Text as="p" variant="bodySm" tone="subdued">
                of open customer invoices
              </Text>
            </div>
            <div>
              <Text as="p" variant="bodySm" tone="subdued">
                Overdue payables
              </Text>
              <Text as="p" fontWeight="semibold">
                {payablesOpen ? formatPercent(payablesOverdue / payablesOpen) : '—'}
              </Text>
              <Text as="p" variant="bodySm" tone="subdued">
                of open supplier invoices
              </Text>
            </div>
            <div>
              <Text as="p" variant="bodySm" tone="subdued">
                DSO
              </Text>
              <Text as="p" fontWeight="semibold">
                {dso === null ? '—' : formatDays(dso)}
              </Text>
              <Text as="p" variant="bodySm" tone="subdued">
                days of sales outstanding
              </Text>
            </div>
            <div>
              <Text as="p" variant="bodySm" tone="subdued">
                DPO
              </Text>
              <Text as="p" fontWeight="semibold">
                {dpo === null ? '—' : formatDays(dpo)}
              </Text>
              <Text as="p" variant="bodySm" tone="subdued">
                days of payables outstanding
              </Text>
            </div>
          </div>
          <div style={{ marginTop: 12 }}>
            <Text as="p" variant="bodySm" tone="subdued">
              Overdue = open amount past the due date; credit notes count as overdue after 30 days. DSO and DPO relate the open amounts to the invoicing (incl. VAT) of the last 12 months.
            </Text>
          </div>
        </div>
      )}
    </Card>
  );
}
